import { useState, type FormEvent } from 'react'
import { subscribeToNewsletter } from '@domain/features/newsletter/newsletter-feature'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import {
  NewsletterPeriodicity,
  type NewsletterPeriodicityValue,
} from '@domain/models/newsletter'
import { toast } from 'sonner'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PERIODICITY_OPTIONS: Array<{
  value: NewsletterPeriodicityValue
  label: string
}> = [
  { value: NewsletterPeriodicity.Daily, label: 'Daily' },
  { value: NewsletterPeriodicity.Weekly, label: 'Weekly' },
  { value: NewsletterPeriodicity.Monthly, label: 'Monthly' },
]

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

function guessUserTimeZone(): string {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim()
    return timeZone && timeZone.length > 0 ? timeZone : 'UTC'
  } catch {
    return 'UTC'
  }
}

export default function NewsletterCard() {
  const [email, setEmail] = useState('')
  const [periodicity, setPeriodicity] = useState<NewsletterPeriodicityValue>(
    NewsletterPeriodicity.Weekly,
  )
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const normalizedEmail = email.trim()
    if (!isValidEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address (e.g. name@example.com).')
      return
    }

    setEmailError(null)
    setIsSubmitting(true)

    try {
      await subscribeToNewsletter({
        email: normalizedEmail,
        periodicity,
        timezone: guessUserTimeZone(),
      })
      toast.success('You are subscribed to the newsletter.')
      setEmail('')
      setPeriodicity(NewsletterPeriodicity.Weekly)
    } catch (error) {
      if (
        error instanceof CoreError &&
        error.cause === CoreErrorCause.EmailAlreadyInUse
      ) {
        toast.error('This email is already subscribed to the newsletter.')
      } else {
        toast.error('Could not subscribe to the newsletter right now.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="rounded-xl border border-violet bg-linear-to-br from-violet to-primary-light p-5 text-white shadow-sm">
      <h3 className="mx-auto w-fit text-xl font-bold text-white">
        Newsletter
      </h3>
      <p className="mt-2 text-sm text-white">
        Subscribe to receive regular emails with the latest news, webinars, Bas
        Bloem podcasts, and Michael Okun posts.
      </p>

      <div aria-hidden="true" className="border-b border-white/40 pt-2.5" />

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div>
          <label
            htmlFor="newsletter-email"
            className="mb-1.5 block text-sm font-semibold text-white"
          >
            Email
          </label>
          <input
            id="newsletter-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              if (emailError) {
                setEmailError(null)
              }
            }}
            placeholder="name@example.com"
            className="w-full rounded-xl border border-white/50 bg-white px-4 py-2.5 text-sm text-text-primary transition-colors placeholder:text-text-secondary focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!"
            aria-invalid={Boolean(emailError)}
          />
          {emailError ? (
            <p className="mt-1 text-sm font-semibold text-orange-200">{emailError}</p>
          ) : null}
        </div>

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-white">
            Frequency
          </legend>
          <div className="flex flex-col gap-2 sm:flex-row">
            {PERIODICITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setPeriodicity(option.value)
                }}
                aria-pressed={periodicity === option.value}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                  periodicity === option.value
                    ? 'border-green bg-green text-white'
                    : 'border-white/50 bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <div aria-hidden="true" className="border-t border-white/40" />

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-xl bg-green px-4 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
    </div>
  )
}
